import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MlMap } from 'maplibre-gl';
import { area, polygon as turfPolygon } from '@turf/turf';
import type { Feature, FeatureCollection, Polygon } from 'geojson';
import { COLORS, ORIGIN } from '../../shared/constants';
import type { Boundary, LngLat } from '../../shared/protocol';
import { MAP_STYLE_URL } from '../map/style';
import { formatArea } from '../util/format';

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

/** Host boundary-draw: tap to drop 3–20 corners, live area readout, soft
 *  warning if implausibly small/large, then lock it as the initial value of Z
 *  (§4.2). Its own map instance so taps map cleanly to vertices. */
export function BoundaryDraw({
  center,
  onCancel,
  onConfirm,
}: {
  center?: LngLat | null;
  onCancel: () => void;
  onConfirm: (b: Boundary) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [points, setPoints] = useState<LngLat[]>([]);
  // once the host starts dropping corners, never yank the camera under them
  const touched = useRef(false);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const c = center ?? ORIGIN;
    const map = new maplibregl.Map({
      container: ref.current,
      style: MAP_STYLE_URL,
      center: [c.lng, c.lat],
      zoom: 15.2,
      minZoom: 13,
      maxZoom: 19,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    // E2E hook (MapView.tsx pattern): expose the draw map for center asserts.
    if (typeof window !== 'undefined') (window as unknown as { __boltDrawMap?: MlMap }).__boltDrawMap = map;
    map.on('load', () => {
      map.addSource('draft', { type: 'geojson', data: EMPTY });
      map.addSource('draft-line', { type: 'geojson', data: EMPTY });
      map.addSource('verts', { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'draft-fill', type: 'fill', source: 'draft', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.14 } });
      map.addLayer({ id: 'draft-line-l', type: 'line', source: 'draft-line', paint: { 'line-color': COLORS.signal, 'line-width': 2, 'line-dasharray': [2, 1.5] } });
      map.addLayer({ id: 'verts-l', type: 'circle', source: 'verts', paint: { 'circle-radius': 5, 'circle-color': COLORS.bone, 'circle-stroke-color': COLORS.slate, 'circle-stroke-width': 2 } });
      map.on('click', (e) => {
        touched.current = true;
        setPoints((p) => [...p, { lat: e.lngLat.lat, lng: e.lngLat.lng }]);
      });
      setLoaded(true);
      map.once('idle', () => ref.current?.setAttribute('data-map-ready', 'true'));
    });
    return () => {
      const w = window as unknown as { __boltDrawMap?: MlMap };
      if (w.__boltDrawMap === map) delete w.__boltDrawMap;
      map.remove();
      mapRef.current = null;
      setLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v12.1 field fix: teams-only lobbies share NO positions, so `center` is
  // null anywhere without a locked selfPin and the map opened on the seed
  // ORIGIN — hosts starting a game elsewhere couldn't find themselves. Ask
  // the DEVICE for its own fix (local-only; nothing new on the wire) and
  // recenter once, unless the host has already started drawing.
  useEffect(() => {
    if (center || typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        if (touched.current) return;
        mapRef.current?.jumpTo({ center: [fix.coords.longitude, fix.coords.latitude], zoom: 15.2 });
      },
      () => {
        /* denied/unavailable — the seed center stands */
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  }, [center]);

  // redraw the draft as points change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const set = (id: string, d: Feature | FeatureCollection) =>
      (map.getSource(id) as maplibregl.GeoJSONSource).setData(d);
    set('verts', {
      type: 'FeatureCollection',
      features: points.map((p) => ({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } })),
    });
    if (points.length >= 2) {
      set('draft-line', {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: [...points, points[0]].map((p) => [p.lng, p.lat]) },
      });
    } else set('draft-line', EMPTY);
    if (points.length >= 3) set('draft', ring(points));
    else set('draft', EMPTY);
  }, [points, loaded]);

  const sqm = points.length >= 3 ? area(ring(points)) : 0;
  const a = formatArea(sqm);
  const mi2 = sqm / 2_589_988.110336;
  const warn = points.length >= 3 ? (mi2 < 0.003 ? 'Quite small' : mi2 > 1.5 ? 'Quite large' : null) : null;
  const canLock = points.length >= 3 && points.length <= 20;

  return (
    <div className="draw-screen">
      <div ref={ref} className="map-full" data-testid="draw-map" />
      <div className="draw-topbar">
        <span className="draw-topbar__hint">Tap to drop corners · 3–20</span>
        <span className="mono" style={{ fontSize: '0.7rem', color: 'var(--bone)' }} data-testid="vert-count">
          {points.length} pts
        </span>
      </div>

      {points.length >= 3 && (
        <div className="draw-readout" data-testid="area-readout">
          <span className="draw-readout__val">
            {a.value} {a.unit}
          </span>
          {warn && <span className="draw-readout__warn">{warn}</span>}
        </div>
      )}

      <div className="draw-actions">
        <button className="btn" disabled={!points.length} onClick={() => setPoints((p) => p.slice(0, -1))}>
          Undo
        </button>
        <button className="btn" disabled={!points.length} onClick={() => setPoints([])}>
          Clear
        </button>
        <button
          className="btn btn--signal"
          disabled={!canLock}
          data-testid="lock-boundary"
          onClick={() => onConfirm(ringFeature(points))}
        >
          Lock play area
        </button>
      </div>
      <button
        className="btn btn--ghost"
        style={{ position: 'absolute', top: 'calc(var(--safe-top) + 4rem)', right: '0.8rem', width: 'auto', zIndex: 60 }}
        onClick={onCancel}
        data-testid="cancel-draw"
      >
        Cancel
      </button>
    </div>
  );
}

function ring(points: LngLat[]): Feature<Polygon> {
  return turfPolygon([[...points, points[0]].map((p) => [p.lng, p.lat])]);
}
function ringFeature(points: LngLat[]): Boundary {
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[...points, points[0]].map((p) => [p.lng, p.lat])] } };
}
